import {
  AccountListItem,
  Autocomplete,
  AutocompleteProps,
  AvatarCurrency,
  BaseTextFieldProps,
  InputAdornment,
  TextField,
} from '@razz-team/ui-web';
import { useCallback, useMemo } from 'react';
import { t } from 'i18next';
import { formatServerMoney } from '@razz-team/utils';
import { ApiAccount } from '../../openapi';

type MuiInputProps = Pick<
  BaseTextFieldProps,
  'name' | 'label' | 'placeholder' | 'inputRef' | 'helperText'
>;
type MuiAutocompleteProps = Omit<
  AutocompleteProps<string, false, false, false>,
  'options' | 'multiple' | 'disableClearable' | 'freeSolo' | 'renderInput'
>;

export type AccountAutocompleteProps = MuiInputProps &
  MuiAutocompleteProps & {
    accounts: ApiAccount[] | undefined;
    disableClearable?: boolean;
  };

export function AccountAutocomplete({
  name,
  label,
  value,
  accounts,
  helperText,
  ...props
}: AccountAutocompleteProps) {
  const options = useMemo(() => accounts?.map((a) => a.id) ?? [], [accounts]);
  const accountValue = useMemo(
    () => (value ? accounts?.find((a) => a.id === value) : null),
    [value, accounts],
  );

  const getOptionLabel = useCallback(
    (option: string) => {
      const account = accounts?.find((a) => a.id === option);
      if (!account) {
        return t('Unknown account');
      }

      const accountName = account.name ?? account.balance.currency;
      const balance = formatServerMoney(account.balance);
      return `${accountName} · ${balance}`;
    },
    [accounts],
  );

  const renderOption = useCallback(
    (optionProps: React.HTMLAttributes<HTMLLIElement>, option: string) => {
      const account = accounts?.find((a) => a.id === option);
      return account ? <AccountListItem account={account} {...optionProps} /> : null;
    },
    [accounts],
  );

  return (
    // TODO: disableClearable
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <Autocomplete
      fullWidth
      // controlled component must have value
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      value={value ?? null}
      options={options}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={label}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: accountValue ? (
              <InputAdornment position="start">
                <AvatarCurrency currency={accountValue.balance.currency} size={34} />
              </InputAdornment>
            ) : null,
          }}
        />
      )}
      {...props}
    />
  );
}
